"use client";

import React from "react";
import { Phone, Mail, ArrowRight, MapPin, ShieldCheck } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { motion, useReducedMotion } from "framer-motion";
const nearbyAreas = [{
  name: "Clapham",
  to: "/AreasWeCoverPage"
}, {
  name: "Richmond",
  to: "/AreasWeCoverPage"
}];
const accreditations = [{
  name: "NASC Full Member"
}, {
  name: "CHAS Accredited"
}];
export default function QuoteCta() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: shouldReduceMotion ? 1 : 0,
      y: shouldReduceMotion ? 0 : 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="finalquotecta" data-nav="light" className="bg-background py-16 sm:py-20 lg:py-24 border-t border-border">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-6xl">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.2
      }} variants={containerVariants} className="rounded-2xl bg-secondary text-secondary-foreground p-8 sm:p-12 lg:p-16 shadow-xl relative overflow-hidden">
          {/* Subtle background graphic element */}
          <div className="absolute -right-24 -top-24 w-96 h-96 rounded-full bg-primary/10 blur-3xl pointer-events-none" />
          <div className="absolute -left-24 -bottom-24 w-96 h-96 rounded-full bg-primary/5 blur-3xl pointer-events-none" />

          <div className="relative z-10 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-center">
            {/* Left Content Column */}
            <div className="lg:col-span-7 space-y-6">
              <div className="flex flex-wrap gap-2">
                {accreditations.map((item, i) => <Badge key={item.name} data-index={i} variant="outline" className="border-secondary-foreground/20 text-secondary-foreground/90 bg-secondary-foreground/5 text-xs py-1 px-2.5 gap-1.5">
                    <ShieldCheck className="w-3.5 h-3.5 text-primary" />
                    <span>{item.name}</span>
                  </Badge>)}
              </div>

              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-secondary-foreground">
                Request a scaffolding quote
              </h2>

              <p className="text-base sm:text-lg text-secondary-foreground/80 max-w-xl leading-relaxed">
                Speak directly with our team to arrange a prompt site survey, discuss access challenges, or receive a formal schedule of rates.
              </p>

              <div className="flex flex-col sm:flex-row flex-wrap gap-4 pt-2">
                <Button asChild size="lg" className="bg-primary hover:bg-primary/90 text-primary-foreground font-semibold px-8 h-12 rounded-md shadow-md text-base">
                  <Link to="/ContactRequestQuotePage">
                    <span>Request a quote</span>
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Link>
                </Button>

                <Button asChild variant="outline" size="lg" className="border-secondary-foreground/20 bg-secondary-foreground/10 text-secondary-foreground hover:bg-secondary-foreground/20 hover:text-secondary-foreground h-12 rounded-md px-6 text-base">
                  <Link to="/AreasWeCoverPage">
                    <span>View all coverage</span>
                  </Link>
                </Button>
              </div>
            </div>

            {/* Right Contact Card Column */}
            <div className="lg:col-span-5">
              <div className="rounded-xl bg-card text-card-foreground p-6 sm:p-8 shadow-md border border-border space-y-6">
                <div className="space-y-1">
                  <span className="text-xs uppercase tracking-wider font-semibold text-muted-foreground">
                    Direct communication
                  </span>
                  <h3 className="text-xl font-bold text-card-foreground">
                    BMF Scaffolding Ltd
                  </h3>
                </div>

                <div className="space-y-4">
                  {/* Phone link */}
                  <a href="tel:01344929900" className="flex items-center gap-4 p-3.5 rounded-lg border border-border bg-muted/30 hover:bg-muted/70 transition-[background-color,border-color] group text-card-foreground hover:text-card-foreground focus:text-card-foreground">
                    <div className="w-10 h-10 rounded-md bg-primary/10 text-primary flex items-center justify-center shrink-0 group-hover:bg-primary group-hover:text-primary-foreground transition-colors">
                      <Phone className="w-5 h-5" />
                    </div>
                    <div>
                      <div className="text-xs font-medium text-muted-foreground">
                        Phone line
                      </div>
                      <div className="text-base font-semibold text-card-foreground">
                        {"01344 929900"}
                      </div>
                    </div>
                  </a>

                  {/* Email link */}
                  <a href="mailto:quotes@bmfscaffolding.co.uk" className="flex items-center gap-4 p-3.5 rounded-lg border border-border bg-muted/30 hover:bg-muted/70 transition-[background-color,border-color] group">
                    <div className="w-10 h-10 rounded-md bg-primary/10 text-primary flex items-center justify-center shrink-0 group-hover:bg-primary group-hover:text-primary-foreground transition-colors">
                      <Mail className="w-5 h-5" />
                    </div>
                    <div className="overflow-hidden">
                      <div className="text-xs font-medium text-muted-foreground">
                        Email enquiries
                      </div>
                      <div className="text-sm sm:text-base font-semibold text-card-foreground truncate">
                        quotes@bmfscaffolding.co.uk
                      </div>
                    </div>
                  </a>
                </div>

                <p className="text-xs text-muted-foreground leading-relaxed pt-1">
                  Serving residential, commercial, industrial, and roofing contractors across Berkshire, Surrey, Hampshire, and London.
                </p>
              </div>
            </div>
          </div>

          <Separator className="my-8 sm:my-10 bg-secondary-foreground/15" />

          {/* Nearby areas listing */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 text-sm text-secondary-foreground/80">
            <div className="flex items-center gap-2 font-medium">
              <MapPin className="w-4 h-4 text-primary shrink-0" />
              <span>Nearby areas:</span>
              <div className="flex items-center gap-3">
                {nearbyAreas.map((area, i) => <React.Fragment key={area.name}>
                    {i > 0 && <span className="text-secondary-foreground/30">•</span>}
                    <Link data-index={i} to={area.to} className="text-secondary-foreground hover:text-primary underline underline-offset-4 transition-colors font-medium">
                      {area.name}
                    </Link>
                  </React.Fragment>)}
              </div>
            </div>

            <span className="text-xs text-secondary-foreground/60">
              BMF Scaffolding Ltd • Operations throughout Home Counties &amp; London
            </span>
          </div>
        </motion.div>
      </div>
    </section>;
}